// Reject-at-submit gate for CaaS Config strings that would trip Nomad's
// client-side task_runner template interpolator.
//
// Mirrors the L2 predicate in `chimera/caas/locals_jobspec.tf` — keep
// SHELL_BINS / DASH_C_FLAGS in sync with `_shell_bins` /
// `_shell_dash_c_flags`. Also mirrors api_io_db's
// `caas_input_validation.py` — same rule, same allowlists.

const SHELL_BINS = new Set(["sh", "bash", "/bin/sh", "/bin/bash", "/usr/bin/sh", "/usr/bin/bash"]);
const DASH_C_FLAGS = new Set(["-c", "-lc", "-Ec", "-ic", "-cl"]);
const METACHAR = /\$\{|%\{/;

function hasMetachar(s: string | null | undefined): boolean {
  return typeof s === "string" && METACHAR.test(s);
}

/** (field, index) of the L2-routed body position, else null. */
function shellBodyPosition(
  entrypoint: string[] | null | undefined,
  args: string[] | null | undefined
): { field: "entrypoint" | "args"; index: number } | null {
  const ep = entrypoint ?? [];
  const ar = args ?? [];
  if (ep.length >= 3 && SHELL_BINS.has(ep[0]) && DASH_C_FLAGS.has(ep[1])) {
    return { field: "entrypoint", index: 2 };
  }
  if (ep.length === 0 && ar.length >= 3 && SHELL_BINS.has(ar[0]) && DASH_C_FLAGS.has(ar[1])) {
    return { field: "args", index: 2 };
  }
  if (ep.length === 2 && SHELL_BINS.has(ep[0]) && DASH_C_FLAGS.has(ep[1]) && ar.length >= 1) {
    return { field: "args", index: 0 };
  }
  return null;
}

export interface ContainerConfigStrings {
  entrypoint?: string[] | null;
  args?: string[] | null;
  command?: string | null;
  image?: string | null;
}

/** Return violation messages; empty list = OK. */
export function validateContainerConfigStrings(input: ContainerConfigStrings): string[] {
  const ep = input.entrypoint ?? [];
  const ar = input.args ?? [];
  const body = shellBodyPosition(ep, ar);
  const violations: string[] = [];

  const check = (name: "entrypoint" | "args", arr: string[]) => {
    arr.forEach((s, i) => {
      if (!hasMetachar(s)) return;
      if (body && body.field === name && body.index === i) return;
      violations.push(
        `${name}[${i}] contains \${...} or %{...}; wrap in ["bash","-c","<script>"] or drop the metachar.`
      );
    });
  };

  check("entrypoint", ep);
  check("args", ar);

  if (hasMetachar(input.command)) {
    violations.push("command contains ${...} or %{...}; not allowed.");
  }
  if (hasMetachar(input.image)) {
    violations.push("image contains ${...} or %{...}; not allowed.");
  }
  return violations;
}

/**
 * Formik-compatible validator for the JSON-encoded entrypoint field on the
 * CaaS create/adjust forms. Returns error message string or undefined.
 * The field is `command` in the form but holds the ENTRYPOINT array as JSON.
 *
 * NOTE: the wired form only classifies S1 (`entrypoint = [<shell>, <-c>, <body>, …]`).
 * S2 (`args=[<shell>, <-c>, <body>]`) and S3 (`entrypoint=[<shell>, <-c>], args=[<body>]`)
 * are covered by the core `validateContainerConfigStrings` helper but do not have
 * dedicated form fields here today — server-side validation (api_io_db) is the
 * authoritative gate for S2/S3 shapes submitted via direct API.
 */
export function validateCommandJson(commandJson: string | undefined): string | undefined {
  if (!commandJson) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(commandJson);
  } catch {
    return "Must be an array of strings in json format";
  }
  if (!Array.isArray(parsed) || !parsed.every((x) => typeof x === "string")) {
    return "Must be an array of strings in json format";
  }
  const violations = validateContainerConfigStrings({ entrypoint: parsed as string[] });
  return violations.length ? violations.join("; ") : undefined;
}

/**
 * Formik-compatible validator for the container image field. Wraps the
 * existing `validateContainerImage` reference-grammar check with the metachar
 * reject.
 */
export function validateContainerImageMetachar(value: string | undefined): string | undefined {
  const violations = validateContainerConfigStrings({ image: value });
  return violations.length ? violations.join("; ") : undefined;
}
